/**
 * Mirrors `CollectionUserPermission` in
 * `pplx/common/core/storage/postgres/database_types.py`.
 *
 * Retired values, never to be reused: 3 (ADMIN), 13 (INVITED_ADMIN),
 * 15 (INVITED_EDITOR).
 */
export var CollectionUserPermission;
(function (CollectionUserPermission) {
    CollectionUserPermission[CollectionUserPermission["NONE"] = 0] = "NONE";
    CollectionUserPermission[CollectionUserPermission["READER"] = 1] = "READER";
    CollectionUserPermission[CollectionUserPermission["WRITER"] = 2] = "WRITER";
    CollectionUserPermission[CollectionUserPermission["EDITOR"] = 5] = "EDITOR";
    CollectionUserPermission[CollectionUserPermission["OWNER"] = 4] = "OWNER";
    CollectionUserPermission[CollectionUserPermission["OWNER_DEFAULT_BOOKMARKS"] = 6] = "OWNER_DEFAULT_BOOKMARKS";
    CollectionUserPermission[CollectionUserPermission["INVITED_READER"] = 11] = "INVITED_READER";
    CollectionUserPermission[CollectionUserPermission["INVITED_WRITER"] = 12] = "INVITED_WRITER";
})(CollectionUserPermission || (CollectionUserPermission = {}));
export function invitedList() {
    return [CollectionUserPermission.INVITED_READER, CollectionUserPermission.INVITED_WRITER];
}
/**
 * The capabilities Projects authorize on. Mirrors `CollectionCapability` in
 * `pplx/common/core/collection/capabilities.py`.
 *
 * There is no `administer`: with the ADMIN tier retired, administering a Space
 * is owning it.
 */
export var CollectionCapability;
(function (CollectionCapability) {
    CollectionCapability["VIEW"] = "view";
    CollectionCapability["EDIT"] = "edit";
    CollectionCapability["OWN"] = "own";
})(CollectionCapability || (CollectionCapability = {}));
const NOTHING = [];
const VIEW = [CollectionCapability.VIEW];
const EDIT = [CollectionCapability.VIEW, CollectionCapability.EDIT];
const OWN = [
    CollectionCapability.VIEW,
    CollectionCapability.EDIT,
    CollectionCapability.OWN,
];
/**
 * The **frontend** truth table, written out per value rather than derived from
 * the comparisons below, so it fails when one of them changes meaning instead
 * of agreeing with it.
 *
 * This now matches the backend table in
 * `pplx/common/core/collection/capabilities.py` for every value. Phase 1d
 * resolved the invited-reader divergence in the backend's favour: INVITED_READER
 * and INVITED_WRITER grant `view`, so a pending invitee can see what they were
 * invited to before accepting. Previously they granted nothing here, which is
 * why `useSpacePermissions` now reports `isReadOnly: true` for an invited user
 * where it used to report false.
 *
 * `view` is the only capability an invite confers. It does not make the invitee
 * a member: `isMember` derives from `atLeastWriter`, and the backend's
 * explicit-membership gate still excludes every INVITED_* value, so no action
 * is permitted until the invite is accepted.
 */
const FRONTEND_CAPABILITIES = {
    [CollectionUserPermission.NONE]: NOTHING,
    [CollectionUserPermission.READER]: VIEW,
    [CollectionUserPermission.WRITER]: EDIT,
    // EDITOR is capability-identical to WRITER; Phase 1d folds it away.
    [CollectionUserPermission.EDITOR]: EDIT,
    [CollectionUserPermission.OWNER]: OWN,
    [CollectionUserPermission.OWNER_DEFAULT_BOOKMARKS]: OWN,
    [CollectionUserPermission.INVITED_READER]: VIEW,
    [CollectionUserPermission.INVITED_WRITER]: VIEW,
};
export function frontendCapabilities(permission) {
    return FRONTEND_CAPABILITIES[permission] ?? NOTHING;
}
/** Whether `permission` grants `capability` on the frontend. */
export function hasCapability(permission, capability) {
    return frontendCapabilities(permission).includes(capability);
}
export function isOwner(permission) {
    return hasCapability(permission, CollectionCapability.OWN);
}
export function isOwnerDefaultBookmarks(permission) {
    return permission === CollectionUserPermission.OWNER_DEFAULT_BOOKMARKS;
}
export function atLeastWriter(permission) {
    return hasCapability(permission, CollectionCapability.EDIT);
}
export function atLeastReader(permission) {
    return hasCapability(permission, CollectionCapability.VIEW);
}
export function isInvite(permission) {
    return invitedList().includes(permission);
}
export var RedirectReason;
(function (RedirectReason) {
    RedirectReason["PageAccessNotAllowed"] = "pageAccessNotAllowed";
    RedirectReason["InvalidThread"] = "invalidThread";
    RedirectReason["ThreadExpired"] = "threadExpired";
    RedirectReason["ThreadStreamError"] = "threadStreamError";
    RedirectReason["CollectionAccessNotAllowed"] = "collectionAccessNotAllowed";
    RedirectReason["SpaceAccessNotAllowed"] = "spaceAccessNotAllowed";
    RedirectReason["InvalidCollection"] = "invalidCollection";
    RedirectReason["InvalidTask"] = "invalidTask";
    RedirectReason["InvalidAsset"] = "invalidAsset";
    // Emitted by the Computer pplx-app authorize endpoint
    // (pplx/backend/web/server/rest_async/rest/computer_pplx_app_auth/routes.py) when a
    // viewer is denied an app-hosted asset. Keep in sync with that producer.
    RedirectReason["WebsiteAccessNotAllowed"] = "websiteAccessNotAllowed";
    // No longer produced: access gates render in place instead of redirecting with
    // this reason. Kept so URLs minted by older bundles (open tabs, history) during
    // a deploy window still show an accurate message rather than "does not exist".
    RedirectReason["ThreadAccessNotAllowed"] = "threadAccessNotAllowed";
    RedirectReason["SubscriptionAlreadyActive"] = "subscriptionAlreadyActive";
    RedirectReason["UnableToGrantAccess"] = "unableToGrantAccess";
})(RedirectReason || (RedirectReason = {}));
export const ENTRY_EXPIRED = 'ENTRY_EXPIRED';
export const VIEW_THREAD_NOT_ALLOWED = 'VIEW_THREAD_NOT_ALLOWED';
export const VIEW_COLLECTION_NOT_ALLOWED = 'VIEW_COLLECTION_NOT_ALLOWED';
export const SPACE_ACCESS_DENIED_FORMER_ORG_OWNER = 'SPACE_ACCESS_DENIED_FORMER_ORG_OWNER';
export const isCollectionAccessDeniedErrorCode = (code) => code === VIEW_COLLECTION_NOT_ALLOWED || code === SPACE_ACCESS_DENIED_FORMER_ORG_OWNER;
//# sourceMappingURL=PrivacyUtils.js.map